<script setup>
import { computed, ref, nextTick } from 'vue';
import {
  CsIcon,
  CsInput,
  CsListbox,
  CsListboxItem,
  CsPopover,
  CsPopoverCard,
  CsPopoverCardContent,
  CsPopoverContent,
  CsPopoverTrigger,
  CsButton,
} from 'dashboard/components/ui/commslayer';
import countries from 'shared/constants/countries.js';
import { useListSelection } from 'dashboard/composables';
import { useI18n } from 'widget/composables/useI18n';

const props = defineProps({
  value: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  label: {
    type: String,
    default: '',
  },
  error: {
    type: String,
    default: '',
  },
  isRequired: {
    type: Boolean,
    default: false,
  },
  isDisabled: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['input', 'blur', 'country-change']);

const { t } = useI18n();

const searchInputRef = ref(null);
const listboxContainerRef = ref(null);
const isPopoverOpen = ref(false);
const searchQuery = ref('');
const selectedCountry = ref(null);
const localPhoneNumber = ref('');

const countryList = computed(() => countries);

const filteredCountries = computed(() => {
  if (!searchQuery.value) return countryList.value;

  const query = searchQuery.value.toLowerCase();
  return countryList.value.filter(country => {
    return (
      country.name.toLowerCase().includes(query) ||
      country.dial_code.includes(query) ||
      country.id.toLowerCase().includes(query)
    );
  });
});

const fullPhoneValue = computed(() => {
  const dialCode = selectedCountry.value ? selectedCountry.value.dial_code : '';
  return `${dialCode}${localPhoneNumber.value}`;
});

function parseValueToCountryAndNumber(value) {
  if (!value) return { country: null, number: '' };

  const country = countries.find(c => value.startsWith(c.dial_code));
  if (country) {
    return {
      country,
      number: value.replace(country.dial_code, ''),
    };
  }

  return { country: null, number: value };
}

function initializeFromValue() {
  if (props.value) {
    const { country, number } = parseValueToCountryAndNumber(props.value);
    selectedCountry.value = country;
    localPhoneNumber.value = number;
  }
}

initializeFromValue();

function handlePhoneInput(value) {
  localPhoneNumber.value = value;

  if (!selectedCountry.value && value.startsWith('+')) {
    const { country, number } = parseValueToCountryAndNumber(value);
    if (country) {
      selectedCountry.value = country;
      localPhoneNumber.value = number;
    }
  }

  emit('input', fullPhoneValue.value);
}

function selectCountry(country) {
  selectedCountry.value = country;
  searchQuery.value = '';
  emit('input', fullPhoneValue.value);
  emit('country-change', country);
}

function closePopover() {
  isPopoverOpen.value = false;
  searchQuery.value = '';
}

function selectCountryAndClose(country) {
  selectCountry(country);
  closePopover();
}

const {
  handleKeydown,
  isItemHovered,
  setCursorPosition,
  adjustScrollPosition,
} = useListSelection({
  items: filteredCountries,
  searchQuery,
  containerRef: listboxContainerRef,
  onSelect: country => {
    selectCountryAndClose(country);
  },
  onClose: closePopover,
});

function handlePopoverOpen() {
  if (props.isDisabled) return;

  isPopoverOpen.value = true;
  nextTick(() => {
    if (searchInputRef.value) {
      searchInputRef.value.focus();
    }

    if (selectedCountry.value) {
      const index = filteredCountries.value.findIndex(
        c => c.id === selectedCountry.value.id
      );
      setCursorPosition(index >= 0 ? index : 0);
      nextTick(() => {
        adjustScrollPosition();
      });
    } else {
      setCursorPosition(0);
    }
  });
}

function handleSearchBlur() {
  if (searchInputRef.value) {
    searchInputRef.value.focus();
  }
}
</script>

<template>
  <CsInput
    :key="selectedCountry ? selectedCountry.id : 'no-country'"
    :value="localPhoneNumber"
    :label="label"
    :placeholder="placeholder"
    :error="error"
    :is-required="isRequired"
    :is-disabled="isDisabled"
    type="tel"
    @input="handlePhoneInput"
    @blur="$emit('blur')"
  >
    <template #prefix>
      <CsPopover
        class="flex"
        :is-manual="true"
        :is-open="isPopoverOpen"
        :offset="8"
        is-teleport
        placement="bottom-start"
        @click-outside="closePopover"
      >
        <CsPopoverTrigger class="flex -ml-1.5">
          <CsButton
            size="sm"
            variant="tertiary"
            class="flex items-center gap-1 !px-1 h-full cursor-pointer flex-none text-sm"
            :is-disabled="isDisabled"
            @click="handlePopoverOpen"
          >
            <span v-if="selectedCountry">
              {{ selectedCountry.emoji }}

              <span v-if="selectedCountry && selectedCountry.dial_code">
                {{ selectedCountry.dial_code }}
              </span>
            </span>
            <CsIcon v-else name="globe" />

            <CsIcon name="chevron-down" size="xs" />
          </CsButton>
        </CsPopoverTrigger>

        <CsPopoverContent>
          <CsPopoverCard>
            <CsPopoverCardContent class="!w-[300px]">
              <div class="p-2">
                <CsInput
                  ref="searchInputRef"
                  v-model="searchQuery"
                  :placeholder="t('PHONE_INPUT.SEARCH_PLACEHOLDER')"
                  size="sm"
                  @blur="handleSearchBlur"
                  @keydown="handleKeydown"
                />
              </div>

              <div
                ref="listboxContainerRef"
                class="max-h-[180px] overflow-y-auto overflow-x-hidden"
              >
                <CsListbox v-if="filteredCountries.length > 0">
                  <CsListboxItem
                    v-for="(country, index) in filteredCountries"
                    :key="country.id"
                    :is-active="
                      selectedCountry && selectedCountry.id === country.id
                    "
                    :is-hovered="isItemHovered(index)"
                    :data-hover="isItemHovered(index)"
                    @mouseenter="setCursorPosition(index)"
                    @click="selectCountryAndClose(country)"
                  >
                    <span>{{ country.emoji }}</span>
                    <span class="flex-1 truncate">{{ country.name }}</span>
                    <span class="text-sm text-brand-text-secondary">
                      ({{ country.dial_code }})
                    </span>
                  </CsListboxItem>
                </CsListbox>

                <div
                  v-else
                  class="py-4 text-center text-sm text-brand-text-secondary"
                >
                  {{ t('PHONE_INPUT.NO_RESULTS') }}
                </div>
              </div>
            </CsPopoverCardContent>
          </CsPopoverCard>
        </CsPopoverContent>
      </CsPopover>
    </template>
  </CsInput>
</template>
